import React, { useMemo, useState } from "react";
import "./GroupEditMsg.css";

function uid(prefix="g") {
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

function Chip({ children, tone="default" }) {
  return <span className={`chip ${tone}`}>{children}</span>;
}

export default function GroupEditMsg({
  title = "Columns have been automatically grouped. You may review, adjust, or confirm the grouping:",
  grouping,
  lowConfidenceItems = [],
  onConfirm,
}) {
  const lowSet = useMemo(() => new Set(lowConfidenceItems || []), [lowConfidenceItems]);

  const [state, setState] = useState(() => ({
    groups: grouping?.groups || [],
    ungrouped: grouping?.ungrouped || [],
    excluded: grouping?.excluded || [],
  }));

  const groupOptions = useMemo(
    () => state.groups.map(g => ({ id: g.id, name: g.name })),
    [state.groups]
  );

  const moveItem = (col, from, to, toGroupId=null) => {
    setState(prev => {
      const next = {
        groups: prev.groups.map(g => ({ ...g, items: [...(g.items || [])] })),
        ungrouped: [...prev.ungrouped],
        excluded: [...prev.excluded],
      };

      const removeFrom = () => {
        if (from === "ungrouped") {
          next.ungrouped = next.ungrouped.filter(x => x !== col);
        } else if (from === "excluded") {
          next.excluded = next.excluded.filter(x => x !== col);
        } else {
          next.groups = next.groups.map(g => {
            if (g.id !== from) return g;
            return { ...g, items: (g.items || []).filter(x => x !== col) };
          });
        }
      };

      const addTo = () => {
        if (to === "ungrouped") {
          if (!next.ungrouped.includes(col)) next.ungrouped.push(col);
        } else if (to === "excluded") {
          if (!next.excluded.includes(col)) next.excluded.push(col);
        } else if (to === "group" && toGroupId) {
          next.groups = next.groups.map(g => {
            if (g.id !== toGroupId) return g;
            const items = g.items || [];
            if (items.includes(col)) return g;
            return { ...g, items: [...items, col] };
          });
        }
      };

      removeFrom();
      addTo();
      return next;
    });
  };

  const renameGroup = (gid, name) => {
    setState(prev => ({
      ...prev,
      groups: prev.groups.map(g => g.id === gid ? { ...g, name } : g),
    }));
  };

  const addGroup = () => {
    setState(prev => ({
      ...prev,
      groups: [
        ...prev.groups,
        { id: uid("g"), name: "New group", items: [], confidence: 1.0, rule: "manual" },
      ]
    }));
  };

  const deleteGroup = (gid) => {
    // 刪除群：items 丟到 excluded
    setState(prev => {
      const g = prev.groups.find(x => x.id === gid);
      const items = g?.items || [];
      const excluded = [...prev.excluded];
      for (const c of items) if (!excluded.includes(c)) excluded.push(c);
      return {
        ...prev,
        excluded,
        groups: prev.groups.filter(x => x.id !== gid),
      };
    });
  };

  const handleConfirm = () => {
    const payload = {
      groups: state.groups.map(g => ({
        ...g,
        items: (g.items || []).filter(Boolean),
      })),
      ungrouped: state.ungrouped.filter(Boolean),
      excluded: state.excluded.filter(Boolean),
    };
    onConfirm?.(payload);
  };

  const SectionHeader = ({ label, count, right }) => (
    <div className="ge-section-header">
      <div className="ge-section-left">
        <span className="ge-section-title">{label}</span>
        <span className="ge-count">({count})</span>
      </div>
      <div className="ge-section-right">{right}</div>
    </div>
  );

  const ItemRow = ({ col, from }) => (
    <div className="ge-item-row">
      <div className="ge-item-name">
        {col}
        {lowSet.has(col) && <Chip tone="warn">low</Chip>}
      </div>

      <div className="ge-item-actions">
        <select
          className="ge-select"
          defaultValue=""
          onChange={(e) => {
            const v = e.target.value;
            e.target.value = "";
            if (!v) return;
            if (v === "ungrouped") moveItem(col, from, "ungrouped");
            else if (v === "excluded") moveItem(col, from, "excluded");
            else moveItem(col, from, "group", v);
          }}
        >
          <option value="" disabled>Move to…</option>
          <option value="ungrouped">Ungrouped</option>
          <option value="excluded">Excluded</option>
          {groupOptions.map(o => (
            <option key={o.id} value={o.id}>Group: {o.name}</option>
          ))}
        </select>

        {from !== "excluded" && (
          <button className="ge-btn" onClick={() => moveItem(col, from, "excluded")}>
            Exclude
          </button>
        )}
        {from !== "ungrouped" && (
          <button className="ge-btn" onClick={() => moveItem(col, from, "ungrouped")}>
            UnGroup
          </button>
        )}
      </div>
    </div>
  );
  
  const [tab, setTab] = useState("groups");

  return (
    <div className="group-edit">
        <div className="ge-title">{title}</div>

        <div className="ge-summary">
        <Chip tone="info">Groups: {state.groups.length}</Chip>
        <Chip tone={state.ungrouped.length ? "warn" : "default"}>Ungrouped: {state.ungrouped.length}</Chip>
        <Chip tone={state.excluded.length ? "warn" : "muted"}>Excluded: {state.excluded.length}</Chip>
        </div>

        <div className="ge-actions-top">
        <button className="ge-btn primary" onClick={handleConfirm}>Confirm groups</button>
        <button className="ge-btn" onClick={addGroup}>+ Add new group</button>
        </div>

        {/* Tabs */}
        <div className="ge-tabs">
        <button
            className={`ge-tab ${tab === "groups" ? "active" : ""}`}
            onClick={() => setTab("groups")}
        >
            Groups
        </button>
        <button
            className={`ge-tab ${tab === "ungrouped" ? "active" : ""}`}
            onClick={() => setTab("ungrouped")}
        >
            Ungrouped
        </button>
        <button
            className={`ge-tab ${tab === "excluded" ? "active" : ""}`}
            onClick={() => setTab("excluded")}
        >
            Excluded
            {state.excluded.length > 0 ? <span className="ge-tab-badge">{state.excluded.length}</span> : null}
        </button>
        </div>

        {/* Tab content */}
        {tab === "groups" && (
        <div className="ge-groups">
            <SectionHeader label="Groups (one variable will be selected from each group for analysis)" count={state.groups.length} />
            {state.groups.map(g => (
            <div key={g.id} className="ge-group-card">
                <div className="ge-group-head">
                <input
                    className="ge-group-name"
                    value={g.name}
                    onChange={(e) => renameGroup(g.id, e.target.value)}
                />
                <div className="ge-group-meta">
                    <Chip tone="info">{(g.items || []).length} cols</Chip>
                    {typeof g.confidence === "number" && (
                    <Chip tone={g.confidence < 0.75 ? "warn" : "info"}>
                        conf {Math.round(g.confidence * 100)}%
                    </Chip>
                    )}
                    <button className="ge-btn danger" onClick={() => deleteGroup(g.id)}>
                    Delete group
                    </button>
                </div>
                </div>

                <div className="ge-list">
                {(g.items || []).length === 0 ? (
                    <div className="ge-empty"> (Empty group) </div>
                ) : (
                    (g.items || []).map(col => (
                    <ItemRow key={col} col={col} from={g.id} />
                    ))
                )}
                </div>
            </div>
            ))}
        </div>
        )}

        {tab === "ungrouped" && (
        <div className="ge-box">
            <SectionHeader label="Ungrouped (not included in analysis unless assigned to a group)" count={state.ungrouped.length} />
            <div className="ge-list">
            {state.ungrouped.length === 0 ? (
                <div className="ge-empty">No columns</div>
            ) : (
                state.ungrouped.map(col => <ItemRow key={col} col={col} from="ungrouped" />)
            )}
            </div>
        </div>
        )}

        {tab === "excluded" && (
        <div className="ge-box">
            <SectionHeader label="Excluded (explicitly excluded from analysis)" count={state.excluded.length} />
            <div className="ge-list">
            {state.excluded.length === 0 ? (
                <div className="ge-empty">No columns</div>
            ) : (
                state.excluded.map(col => <ItemRow key={col} col={col} from="excluded" />)
            )}
            </div>
        </div>
        )}
    </div>
    );
}
